import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { Button } from './Button'

export type BannerTone = 'bad' | 'warn'

export interface BannerProps extends HTMLAttributes<HTMLDivElement> {
  /** No default: the two tones mean different things to a screen reader
   *  (`alert` versus `status`) and a caller should say which on purpose. */
  tone: BannerTone
  /** An optional second action — several call sites want "Retry". */
  action?: {
    label: string
    onClick: () => void
  }
  children: ReactNode
}

/**
 * The inline error or warning: it sits in the layout next to the thing that
 * failed, and stays until the state that caused it changes.
 *
 * Not `Toast`. `Toast` is transient and global and disappears on its own —
 * reach for it after an action that *succeeded* (an undoable save). `Banner`
 * is the opposite: it appears because something needs the reader's attention
 * until they do something about it, which is also why it announces itself
 * (`role="alert"` for `bad`, `role="status"` for `warn`) rather than waiting
 * to be read.
 */
export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { tone, action, children, className = '', ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      role={tone === 'bad' ? 'alert' : 'status'}
      className={`u-banner u-banner--${tone} ${className}`.trim()}
      {...rest}
    >
      <p className="u-banner__text">{children}</p>
      {action && (
        <Button variant="quiet" size="sm" className="u-banner__action" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  )
})
